<template>
  <admin-navbar>
    <h1 class="font-bold text-lg">Файл</h1>
    <div class="bg-white rounded shadow">
      <div
        class="flex justify-between border-t-4 border-t-blue-500 rounded-t border-b p-3"
      >
        <h6 class="text-base">Тогтвортой Уул уурхай</h6>
        <button
          @click="toggleEdit()"
          class="bg-blue-500 p-2 text-white text-xs rounded hover:bg-blue-600"
        >
          <font-awesome-icon
            :icon="!isEdit ? 'fa-solid fa-add' : 'fa-solid fa-angles-left'"
            class="mr-1"
          />
          {{ !isEdit ? "Шинээр нэмэх" : "Буцах" }}
        </button>
      </div>

      <section v-if="!isEdit" class="p-3 space-y-5">
        <div class="flex justify-between items-center text-xs">
          <div class="space-x-3">
            <span>Хэл:</span>
            <select
              name="language"
              id="language"
              class="border rounded p-1 text-blue-500"
              v-model="selectedLanguage"
            >
              <option value="mn">Монгол</option>
              <option value="en">Англи</option>
            </select>
          </div>

          <div class="space-x-3">
            <span>Хуудас бүрт:</span>
            <select
              name="pagePerPage"
              id="pagePerPage"
              class="border rounded p-1 text-blue-500"
              v-model="dataPerPage"
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
            </select>
          </div>

          <span>Нийт: {{ totalCount }} </span>

          <div class="flex items-center gap-3">
            <span>Нийт хуудас: {{ totalPages }} </span>
            <PaginationCart
              :totalPages="totalPages"
              :modelValue="currentPage"
              :style="{
                color: 'blue-500',
                height: 7,
                width: 7,
                fontSize: 'xs',
              }"
              @page-changed="onPageChanged"
            />
          </div>
        </div>

        <div
          v-if="documents.length > 0 && !isLoading"
          class="h-[70vh] overflow-y-auto"
        >
          <table class="w-full table-auto border border-gray-300 text-sm">
            <thead class="bg-gray-100">
              <tr>
                <th class="border px-4 py-2">№</th>
                <th class="border px-4 py-2">Үйлдэл</th>
                <th class="border px-4 py-2">Зураг</th>
                <th class="border px-4 py-2">Файлын нэр</th>
                <th class="border px-4 py-2">Файл</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(doc, index) in documents"
                :key="index"
                class="text-center"
              >
                <td class="border p-2">{{ index + 1 }}</td>
                <td class="border p-2 text-xs">
                  <div class="flex justify-center gap-2">
                    <button
                      @click="editDocument(doc)"
                      class="bg-sky-500 text-white px-2 py-1 rounded"
                    >
                      Засах
                    </button>
                    <button
                      @click="deleteDocument(doc.id)"
                      class="bg-red-500 text-white px-2 py-1 rounded"
                    >
                      Устгах
                    </button>
                  </div>
                </td>
                <td class="border p-2">
                  <div class="flex flex-cols justify-center gap-2">
                    <a
                      v-for="(poster, i) in doc.posters"
                      :key="i"
                      :href="poster"
                      target="_blank"
                    >
                      <img
                        :src="poster"
                        alt="Зураг"
                        class="w-16 object-cover rounded"
                      />
                    </a>
                  </div>
                </td>
                <td class="border p-2">{{ doc.title }}</td>
                <td class="border p-2">
                  <a
                    :href="doc.pdf"
                    target="_blank"
                    class="text-blue-500 underline hover:cursor-pointer"
                    >Үзэх</a
                  >
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div
          v-else-if="documents.length === 0 && !isLoading"
          class="flex justify-center items-center h-[70vh]"
        >
          Жагсаалт хоосон байна
        </div>

        <div v-else class="flex justify-center items-center h-[70vh]">
          <SpinLoading :color="'rgb(59 130 246)'" />
        </div>
      </section>

      <YellowEditSustainable
        v-else
        :document="selectedDocument"
        @fetchSustainableMining="fetchSustainableMining"
      />
    </div>
  </admin-navbar>

  <ErrorModal v-if="error" :error="error" />
</template>

<script>
import AdminNavbar from "@/components/admin/AdminNavbar.vue";
import SpinLoading from "@/components/ui/SpinLoading.vue";
import ErrorModal from "@/components/ui/ErrorModal.vue";
import PaginationCart from "@/components/ui/PaginationCart.vue";
import adminInstance from "@/lib/adminInstance";
import { onMounted, ref, watch } from "vue";
import YellowEditSustainable from "@/containers/MiningMongolia/admin/YellowEditSustainable.vue";

export default {
  name: "MMAdminSustainableMining",
  components: {
    AdminNavbar,
    SpinLoading,
    ErrorModal,
    PaginationCart,
    YellowEditSustainable,
  },
  setup() {
    const documents = ref([]);
    const isLoading = ref(false);
    const error = ref(null);

    const dataPerPage = ref(10);
    const totalCount = ref(0);
    const totalPages = ref(1);
    const currentPage = ref(1);

    const selectedLanguage = ref("mn");
    const isEdit = ref(false);
    const selectedDocument = ref(null);

    const toggleEdit = () => {
      isEdit.value = !isEdit.value;
      if (!isEdit.value) {
        selectedDocument.value = null;
      }
    };

    const fetchSustainableMining = async () => {
      try {
        isLoading.value = true;
        const res = await adminInstance.get(
          `/get/sustainable-mining?lang=${selectedLanguage.value}&page=${currentPage.value}&limit=${dataPerPage.value}`
        );
        if (res.status === 200 && res.data.mining) {
          documents.value = res.data.mining;
          totalPages.value = res.data.totalPages;
          totalCount.value = res.data.total;
        }
      } catch (err) {
        error.value = "Алдаа гарлаа.";

        setTimeout(() => {
          error.value = null;
        }, 3000);
      } finally {
        isLoading.value = false;
      }
    };

    onMounted(() => {
      fetchSustainableMining();
    });

    watch([() => dataPerPage.value, () => selectedLanguage.value], () => {
      currentPage.value = 1;
      fetchSustainableMining();
    });

    const onPageChanged = (page) => {
      currentPage.value = page;
      fetchSustainableMining();
    };

    const editDocument = (document) => {
      if (document) {
        selectedDocument.value = document;
      }

      toggleEdit();
    };

    const deleteDocument = async (docId) => {
      if (confirm("Та энэ файлыг устгахдаа итгэлтэй байна уу?")) {
        try {
          const res = await adminInstance.delete(
            `/delete/sustainable-mining?id=${docId}`
          );
          if (res.status === 200) {
            documents.value = documents.value.filter((d) => d.id !== docId);
          }
        } catch (err) {
          error.value = "Устгахад алдаа гарлаа.";
          setTimeout(() => {
            error.value = null;
          }, 3000);
        }
      }
    };

    return {
      documents,
      error,
      isLoading,
      editDocument,
      deleteDocument,
      dataPerPage,
      currentPage,
      totalCount,
      totalPages,
      onPageChanged,
      selectedLanguage,
      isEdit,
      selectedDocument,
      fetchSustainableMining,
      toggleEdit,
    };
  },
};
</script>
